<template>
    <div>
        <b-data-table
            :headers="headersNotifications"
            :items="formattedNotifications"
            :page.sync="page"
            :loading="loading"
            class="b-outlined"
            :pagination="notificationsPagination"
            @page-sync="p=>page=p"
            disable-sort
        >
            <!-- Body Column -->
            <template v-slot:item.body="{ item }">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span @click="showBodyDetails(item)" style="cursor: pointer;">
                        <template v-if="typeof item.body === 'object'">
                            <!-- Dynamically pick the first key in the object -->
                            {{ extractFirstKeyValue(item.body) | truncate(25, '...') }}
                        </template>
                        <template v-else>
                            {{ item.body | truncate(25, '...') }}
                        </template>
                    </span>
                </div>
            </template>
            <!-- Identifier Column -->
            <template v-slot:item.identifier="{ item }">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                    <span @click="showIdentifierDetails(item)" style="cursor: pointer;">
                        {{ item.identifier | truncate(20, "...") }}
                    </span>
                </div>
            </template>
            <!-- Campaign Title Column -->
            <template v-slot:item.campaign_title="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">
                          {{ item.campaign_title | truncate(30, "...") }}
                        </span>
                    </template>
                    <span>{{ item.campaign_title }}</span>
                </v-tooltip>
            </template>
            <!-- Data Column -->
            <template v-slot:item.data="{ item }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            v-bind="attrs"
                            v-on="on"
                            color="secondary darken-3"
                            icon
                            @click="showDataDetails(item)"
                        >
                            <v-icon v-text="icons.mdiMenuOpen"></v-icon>
                        </v-btn>
                    </template>
                    <span>{{ translations.show_details[lang] }}</span>
                </v-tooltip>
            </template>
            <!-- Created Column -->
            <template v-slot:item.created_at="{ item }">
                <v-tooltip left>
                    <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{
                    (item.created_at &&
                        new Date(item.created_at).toLocaleDateString(
                            dateLang,
                            dateOptions
                        )) ||
                    ""
                }}
            </span>
                    </template>
                    <span>{{ item.created_at }}</span>
                </v-tooltip>
            </template>
        </b-data-table>

        <NotificationsCustomDialog
            v-if="dataDetailsDialog"
            :isVisible="dataDetailsDialog"
            :content="selectedNotificationData"
            :max-width="600"
            @update:isVisible="dataDetailsDialog = $event"
        />
        <NotificationsCustomDialog
            v-if="bodyDetailsDialog"
            :isVisible="bodyDetailsDialog"
            :content="selectedNotificationBody"
            @update:isVisible="bodyDetailsDialog = $event"
        />

        <NotificationsCustomDialog
            v-if="identifierDetailsDialog"
            :isVisible="identifierDetailsDialog"
            :content="selectedNotificationIdentifier"
            :max-width="350"
            @update:isVisible="identifierDetailsDialog = $event"
        />
    </div>

</template>

<script>
import { mapState } from "vuex";
import translations from "@/utils/translations/loyaltyPanel/customers";
import {
    mdiInformationVariant,
    mdiMore,
    mdiMenuOpen,
    mdiUnfoldMoreVertical,
    mdiTextBoxOutline
} from "@mdi/js";
import NotificationsCustomDialog from "@/components/loyaltyPanel/customers/NotificationsCustomDialog.vue";

export default {
    name: "NotificationsTable",
    mixins: [translations],
    components: {
        NotificationsCustomDialog
    },
    props: {
        systemNotifications: {
            type: Array,
            required: true
        },
        notificationCategories: {
            type: Array,
            required: true
        }
    },
    filters: {
        truncate: function(text, length, suffix) {
            if (text.length > length) {
                return text.substring(0, length) + suffix;
            } else {
                return text;
            }
        }
    },
    data() {
        return {
            page: +this.$route.query.page,
            dataDetailsDialog: false,
            bodyDetailsDialog: false,
            identifierDetailsDialog: false,
            selectedNotificationData: null,
            selectedNotificationBody: null,
            selectedNotificationIdentifier: null,
            icons: {
                mdiInformationVariant,
                mdiMore,
                mdiMenuOpen,
                mdiUnfoldMoreVertical,
                mdiTextBoxOutline
            },
            dateOptions: { year: "numeric", month: "long", day: "numeric" }
        };
    },
    computed: {
        ...mapState("loyaltyPanel/notifications", [
            "notifications", "notificationsPagination"
        ]),
        ...mapState([
            "loading",
            "errorMessage",
            "serverItemsLength",
            "superUser"
        ]),
        headersNotifications() {
            let headers =  [
                {
                    text: "ID",
                    value: "user_notification_id",
                    width: 100
                },
                {
                    text: this.translations.user_id[this.lang],
                    value: "user_id",
                    width: 100
                },
                {
                    text: this.translations.mobile[this.lang],
                    value: "mobile",
                    width: 130
                },
                {
                    text: this.translations.campaign_title[this.lang],
                    value: "campaign_title",
                    width: 200
                },
                {
                    text: this.translations.system_notification_type[this.lang],
                    value: "system_notification_type",
                    width: 150
                },
                {
                    text: this.translations.notification_category_system[this.lang],
                    value: "notification_category_system",
                    width: 150
                },
                {
                    text: this.translations.body[this.lang],
                    value: "body",
                    width: 250
                },
                {
                    text: this.translations.type[this.lang],
                    value: "type",
                    width: 100
                },
                {
                    text: this.translations.status[this.lang],
                    value: "status",
                    width: 100
                },
                {
                    text: this.translations.created[this.lang],
                    value: "created_at",
                    width: 100
                }
            ];

            if (this.superUser) {
                headers.splice(7, 0, {
                        text: this.translations.identifier[this.lang],
                        value: "identifier",
                        width: 100
                    }
                );
                
                headers.splice(9, 0, {
                        text: this.translations.data[this.lang],
                        value: "data",
                        width: 100
                    }
                );
            }
            return headers
        },
        lang() {
            return this.$route.params.lang;
        },
        formattedNotifications() {
            return this.notifications.map(notification => {
                //const systemNotification = this.systemNotifications.find(systemNotification => systemNotification.type === notification.system_notification_type);
                const notificationCategory = this.notificationCategories.find(notificationCategory => notificationCategory.system === notification.notification_category_system);

                return {
                    ...notification,
                    //system_notification_type: systemNotification ? systemNotification.displayName : notification.system_notification_type,
                    notification_category_system: notificationCategory ? notificationCategory.name : notification.notification_category_system,
                    data: JSON.stringify(notification.data)
                };
            });
        },
        dateLang() {
            let lang = "en-US";
            if (this.lang === "el") {
                lang = "el-GR";
            } else if (this.lang === "it") {
                lang = "it-IT";
            }
            return lang;
        }
    },
    watch: {
        page(newPage) {
            // Emit the page change to the parent, but don't handle routing here
            this.$emit("page-changed", newPage);
        }
    },
    methods: {
        showDataDetails(item) {
            this.selectedNotificationData = JSON.parse(item.data);
            this.dataDetailsDialog = true;
        },
        showBodyDetails(item) {
            this.selectedNotificationBody = item.body;
            this.bodyDetailsDialog = true;
        },
        showIdentifierDetails(item) {
            this.selectedNotificationIdentifier = item.identifier;
            this.identifierDetailsDialog = true;
        },
        extractFirstKeyValue(bodyObject) {
            // Get all the keys from the object
            const keys = Object.keys(bodyObject);

            // If the object has keys, return the value of the first key
            if (keys.length > 0) {
                return bodyObject[keys[0]];  // You can adjust this logic to choose a specific key if needed
            }

            // Fallback if the object is empty or no suitable key is found
            return 'No content available';
        },
    }
};
</script>
